import GrowChart from './GrowChart';
import { useFixedPopover } from 'utilities/useFixedPopover';
import { useState, useMemo, useRef, useEffect } from 'react';
import { MeasurementMarkerDto, SessionKindGroupName } from 'types/models/SessionsPageData';
import { t } from 'utilities/TranslationUtils';
import { darkTooltip, isBinaryOnTargetKind, measurementResultBucket } from 'utilities/chartUtils';
import { buildHeatmapCells, HeatmapCell } from './playerPerformanceUtils';

type MetricKey =
  | 'ballSpeedAvg'
  | 'distanceAvg'
  | 'hangTimeAvg'
  | 'gFactor'
  | 'spinRate'
  | 'spinAxis'
  | 'launchAngle'
  | 'launchDirection'
  | 'pullDirection'
  | 'maxHeight'
  | 'goalCrossingSpeed'
  | 'wallCrossingHeight';
type ChartMode = 'scatter' | 'heatmap';

const PRESETS: ReadonlyArray<{ labelKey: string; x: MetricKey; y: MetricKey }> = [
  { labelKey: 'playerPerformance.comparePreset_speedDistance',       x: 'ballSpeedAvg', y: 'distanceAvg'   },
  { labelKey: 'playerPerformance.comparePreset_speedHangTime',       x: 'ballSpeedAvg', y: 'hangTimeAvg'   },
  { labelKey: 'playerPerformance.comparePreset_speedLaunchAngle',    x: 'ballSpeedAvg', y: 'launchAngle'   },
  { labelKey: 'playerPerformance.comparePreset_gFactorSpeed',        x: 'gFactor',      y: 'ballSpeedAvg'  },
  { labelKey: 'playerPerformance.comparePreset_spinRateLaunchAngle', x: 'spinRate',     y: 'launchAngle'   },
  { labelKey: 'playerPerformance.comparePreset_hangTimeDistance',    x: 'hangTimeAvg',  y: 'distanceAvg'   },
];

const HEATMAP_BINS = 10;

const METRIC_FIXED_DOMAIN: Partial<Record<MetricKey, { min: number; max: number }>> = {
  ballSpeedAvg: { min: 50, max: 160 },
  distanceAvg:  { min: 7,  max: 40  },
};

// Fixed heatmap bin intervals: distance 1 m, wall crossing 0.2 m (20 cm), pull direction 10°.
// Metrics not listed here fall back to auto "nice" binning.
const METRIC_FIXED_BIN_STEP: Partial<Record<MetricKey, number>> = {
  distanceAvg:        1,
  wallCrossingHeight: 0.2,
  pullDirection:      10,
};

// One group per visible kick status in compare charts.
const RESULT_GROUPS = [
  { key: 'Goal',      labelKey: 'sessions.kickResult_Goal' },
  { key: 'OnTarget',  labelKey: 'sessions.kickResult_OnTarget' },
  { key: 'Saved',     labelKey: 'sessions.kickResult_Saved' },
  { key: 'Savable',   labelKey: 'sessions.kickResult_Savable' },
  { key: 'WallHit',   labelKey: 'sessions.kickResult_WallHit' },
  { key: 'OffTarget', labelKey: 'sessions.kickResult_OffTarget' },
  { key: 'Grounded',  labelKey: 'sessions.kickResult_Grounded' },
] as const;
type ResultKey = (typeof RESULT_GROUPS)[number]['key'];

// Scatter draw order (higher = on top): goal on top, then on target/saved, wall hit, off target, grounded.
const RESULT_DRAW_Z: Record<ResultKey, number> = { Goal: 6, OnTarget: 5, Saved: 4, Savable: 4, WallHit: 3, OffTarget: 2, Grounded: 1 };

function getKickMetric(m: MeasurementMarkerDto, key: MetricKey): number | null {
  switch (key) {
    case 'ballSpeedAvg':       return m.ballSpeed ?? null;
    case 'hangTimeAvg':        return m.hangTime ?? null;
    case 'distanceAvg':        return m.distance ?? null;
    case 'gFactor':            return m.gFactor ?? null;
    case 'spinRate':           return m.spinRate ?? null;
    case 'spinAxis':           return m.spinAxis ?? null;
    case 'launchAngle':        return m.launchAngle ?? null;
    case 'launchDirection':    return m.launchDirection ?? null;
    case 'pullDirection':      return m.pullDirection ?? null;
    case 'maxHeight':          return m.maxHeight ?? null;
    case 'goalCrossingSpeed':  return m.goalCrossingSpeed ?? null;
    case 'wallCrossingHeight': return m.wallCrossingHeight ?? null;
  }
}

function snapNiceDown(v: number): number {
  if (v === 0) return 0;
  if (Math.abs(v) < 1e-10) return 0;
  const sign = v < 0 ? -1 : 1;
  const abs = Math.abs(v);
  const magnitude = Math.pow(10, Math.floor(Math.log10(abs)));
  return sign * Math.floor(abs / magnitude) * magnitude;
}

function snapNiceUp(v: number): number {
  if (v === 0) return 0;
  if (Math.abs(v) < 1e-10) return 0;
  const sign = v < 0 ? -1 : 1;
  const abs = Math.abs(v);
  const magnitude = Math.pow(10, Math.floor(Math.log10(abs)));
  return sign * Math.ceil(abs / magnitude) * magnitude;
}

function formatAxisValue(v: number): string {
  if (v === 0) return '0';
  const abs = Math.abs(v);
  if (abs >= 100) return `${Math.round(v)}`;
  if (abs >= 10)  return `${+v.toFixed(1)}`;
  if (abs >= 1)   return `${+v.toFixed(2)}`;
  if (abs >= 0.01) return `${+v.toFixed(3)}`;
  return v.toExponential(2);
}

function toResultKey(measurement: MeasurementMarkerDto, kind?: SessionKindGroupName): ResultKey {
  const r = (measurement.result ?? '').toLowerCase();
  if (r === 'saved') return 'Saved';
  if (r === 'savable') return 'Savable';
  if (r === 'wallhit') return 'WallHit';
  if (r === 'grounded') return 'Grounded';

  const bucket = measurementResultBucket(measurement, kind ?? null);
  if (bucket === 'goal') return 'Goal';
  if (bucket === 'onTarget') return 'OnTarget';

  // Fall back to explicit string categories for non-bucketed legacy values.
  if (r === 'goal' || r === 'unsavable' || r === 'savable') return 'Goal';
  return 'OffTarget';
}

interface MetricsCompareChartProps {
  measurements: MeasurementMarkerDto[];
  kind?: SessionKindGroupName;
  onShowInTable?: (m: MeasurementMarkerDto) => void;
}

type ScatterPoint = { value: [number, number]; measurementId: string };

const MetricsCompareChart: React.FunctionComponent<MetricsCompareChartProps> = ({
  measurements, kind, onShowInTable,
}) => {
  const [xMetric, setXMetric] = useState<MetricKey>('ballSpeedAvg');
  const [yMetric, setYMetric] = useState<MetricKey>('distanceAvg');
  const [mode, setMode] = useState<ChartMode>('scatter');
  // The X / Y pickers are only shown for a custom pairing; the presets already say what is plotted.
  const [customOpen, setCustomOpen] = useState(false);
  const customHostRef = useRef<HTMLSpanElement>(null);
  const customPlacement = useFixedPopover(customOpen, customHostRef, 260);
  useEffect(() => {
    if (!customOpen) return undefined;
    const onDown = (e: MouseEvent) => { if (!customHostRef.current?.contains(e.target as Node)) setCustomOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCustomOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [customOpen]);
  const [hiddenResults, setHiddenResults] = useState<Set<ResultKey>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const resultColors = useMemo<Record<ResultKey, string>>(() => ({
    Goal: '#009900',
    OnTarget: kind != null && isBinaryOnTargetKind(kind) ? '#009900' : '#D0021B',
    Saved: '#FBBF24',
    Savable: '#FBBF24',
    WallHit: '#FE9800',
    OffTarget: '#707070',
    Grounded: '#222222',
  }), [kind]);

  // Penalties: distance is constant (fixed spot) and spin rate is withheld by services (inaccurate),
  // so hide both metrics and steer the axes off them.
  // Goalkeeper: distance (incoming cross/throw) is not reliably available in the data,
  // so hide it to avoid filtering out all measurements.
  const hideDistance = kind === 'Penalty' || kind === 'Goalkeeper';
  const hideSpin = kind === 'Penalty';
  useEffect(() => {
    if (kind !== 'Penalty' && kind !== 'Goalkeeper') return;
    const shouldReset = (metric: MetricKey) => {
      if (kind === 'Goalkeeper' && metric === 'distanceAvg') return true;
      if (kind === 'Penalty' && (metric === 'distanceAvg' || metric === 'spinRate')) return true;
      return false;
    };
    setXMetric((x) => (shouldReset(x) ? 'ballSpeedAvg' : x));
    setYMetric((y) => (shouldReset(y) ? 'hangTimeAvg' : y));
  }, [kind]);

  const toggleResult = (key: ResultKey) => {
    setHiddenResults((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const measurementById = useMemo(() => {
    const map = new Map<string, MeasurementMarkerDto>();
    for (const m of measurements) map.set(m.id, m);
    return map;
  }, [measurements]);

  // Button clicks inside ECharts tooltip bubble up to the container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleClick = (e: MouseEvent) => {
      const btn = (e.target as Element).closest('[data-action]');
      if (!btn) return;
      const mid = btn.getAttribute('data-mid');
      const action = btn.getAttribute('data-action');
      if (!mid) return;
      const m = measurementById.get(mid);
      if (!m) return;
      if (action === 'table') onShowInTable?.(m);
    };
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, [measurementById, onShowInTable]);

  // One circle per measurement, colored by result. Kicks missing either metric are skipped.
  const kickScatterByResult = useMemo(() => {
    const groups: Record<ResultKey, ScatterPoint[]> = { Goal: [], OnTarget: [], Saved: [], Savable: [], WallHit: [], OffTarget: [], Grounded: [] };
    for (const m of measurements) {
      const xVal = getKickMetric(m, xMetric);
      const yVal = getKickMetric(m, yMetric);
      if (xVal == null || yVal == null || isNaN(xVal) || isNaN(yVal)) continue;
      const key: ResultKey = toResultKey(m, kind);
      groups[key].push({ value: [xVal, yVal], measurementId: m.id });
    }
    return groups;
  }, [measurements, xMetric, yMetric, kind]);

  // Domain from ALL kicks (ignoring hiddenResults) so axes stay stable while toggling.
  // Per-metric fixed domains override the data-driven calculation.
  const kickDomain = useMemo(() => {
    const xFixed = METRIC_FIXED_DOMAIN[xMetric];
    const yFixed = METRIC_FIXED_DOMAIN[yMetric];
    if (xFixed && yFixed) {
      return { xMin: xFixed.min, xMax: xFixed.max, yMin: yFixed.min, yMax: yFixed.max };
    }
    const allPoints = (Object.values(kickScatterByResult) as ScatterPoint[][]).flat();
    if (allPoints.length === 0) return { xMin: 0, xMax: 100, yMin: 0, yMax: 100 };
    const xs = allPoints.map((p) => p.value[0]);
    const ys = allPoints.map((p) => p.value[1]);
    const xDataMin = Math.min(...xs), xDataMax = Math.max(...xs);
    const yDataMin = Math.min(...ys), yDataMax = Math.max(...ys);
    const xRange = xDataMax - xDataMin || xDataMax || 1;
    const yRange = yDataMax - yDataMin || yDataMax || 1;
    const PAD = 0.18;
    return {
      xMin: xFixed ? xFixed.min : (xDataMin >= 0 ? 0 : snapNiceDown(xDataMin - xRange * PAD)),
      xMax: xFixed ? xFixed.max : snapNiceUp(xDataMax + xRange * PAD),
      yMin: yFixed ? yFixed.min : (yDataMin >= 0 ? 0 : snapNiceDown(yDataMin - yRange * PAD)),
      yMax: yFixed ? yFixed.max : snapNiceUp(yDataMax + yRange * PAD),
    };
  }, [kickScatterByResult, xMetric, yMetric]);

  // Filtered kick points for heatmap — respects hiddenResults, skips kicks with missing metric values
  const filteredKickPoints = useMemo(() => {
    const points: [number, number][] = [];
    for (const m of measurements) {
      const resultKey: ResultKey = toResultKey(m, kind);
      if (hiddenResults.has(resultKey)) continue;
      const xVal = getKickMetric(m, xMetric);
      const yVal = getKickMetric(m, yMetric);
      if (xVal == null || yVal == null || isNaN(xVal) || isNaN(yVal)) continue;
      points.push([xVal, yVal]);
    }
    return points;
  }, [measurements, hiddenResults, xMetric, yMetric, kind]);

  // [count, xStart, xEnd, yStart, yEnd] — actual data-coordinate edges so axes match scatter
  const heatmapData = useMemo(
    () => buildHeatmapCells(filteredKickPoints, kickDomain, HEATMAP_BINS, {
      x: METRIC_FIXED_BIN_STEP[xMetric],
      y: METRIC_FIXED_BIN_STEP[yMetric],
    }),
    [filteredKickPoints, kickDomain, xMetric, yMetric],
  );

  const scatterOption = useMemo(() => {
    const xName = t('playerPerformance.metric_' + xMetric);
    const yName = t('playerPerformance.metric_' + yMetric);
    return {
      tooltip: {
        ...darkTooltip('item'),
        hideDelay: 1200,
        formatter: (p: { seriesName: string; value: [number, number]; data: ScatterPoint }) => {
          const mid = p.data?.measurementId ?? '';
          const btns = onShowInTable
            ? `<div style="margin-top:7px;display:flex;gap:5px;flex-wrap:wrap">
                <button class="pp-scatter-tooltip-btn" data-action="table" data-mid="${mid}">${t('playerPerformance.compareShowInTable')}</button>
               </div>`
            : '';
          return `<div style="min-width:140px"><b>${p.seriesName}</b><br/>${xName}: <b>${formatAxisValue(p.value[0])}</b><br/>${yName}: <b>${formatAxisValue(p.value[1])}</b>${btns}</div>`;
        },
      },
      grid: { top: 16, bottom: 52, left: 72, right: 16, containLabel: false },
      xAxis: {
        type: 'value' as const,
        min: kickDomain.xMin,
        max: kickDomain.xMax,
        name: xName,
        nameLocation: 'middle' as const,
        nameGap: 28,
        axisLabel: { fontSize: 11, color: '#6B7280', formatter: formatAxisValue },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
      },
      yAxis: {
        type: 'value' as const,
        min: kickDomain.yMin,
        max: kickDomain.yMax,
        name: yName,
        nameLocation: 'middle' as const,
        nameGap: 40,
        axisLabel: { fontSize: 11, color: '#6B7280', formatter: formatAxisValue },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
      },
      series: RESULT_GROUPS.map((g) => ({
        type: 'scatter' as const,
        name: t(g.labelKey),
        data: hiddenResults.has(g.key) ? [] : kickScatterByResult[g.key],
        symbolSize: 11,
        itemStyle: { color: resultColors[g.key], opacity: 0.85 },
        // Draw order (top → bottom): goal > on target > off target.
        z: RESULT_DRAW_Z[g.key],
      })),
    };
  }, [kickScatterByResult, kickDomain, hiddenResults, resultColors, xMetric, yMetric]);

  const heatmapOption = useMemo(() => {
    const xLabel = t('playerPerformance.metric_' + xMetric);
    const yLabel = t('playerPerformance.metric_' + yMetric);
    const maxCount = Math.max(...heatmapData.map((c) => c[0]), 1);
    return {
      tooltip: {
        ...darkTooltip('item'),
        hideDelay: 600,
        formatter: (params: any) => {
          const [count, xStart, xEnd, yStart, yEnd] = (params.value ?? params.data) as HeatmapCell;
          const countLabel = t(count === 1 ? 'playerPerformance.compareKickCountOne' : 'playerPerformance.compareKickCountMany', { count });
          return `<div style="min-width:140px">${xLabel}: <b>${formatAxisValue(xStart)}–${formatAxisValue(xEnd)}</b><br/>${yLabel}: <b>${formatAxisValue(yStart)}–${formatAxisValue(yEnd)}</b><br/>${countLabel}</div>`;
        },
      },
      grid: { top: 16, bottom: 52, left: 72, right: 16, containLabel: false },
      xAxis: {
        type: 'value' as const,
        min: kickDomain.xMin,
        max: kickDomain.xMax,
        name: xLabel,
        nameLocation: 'middle' as const,
        nameGap: 28,
        axisLabel: { fontSize: 11, color: '#6B7280', formatter: formatAxisValue },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
      },
      yAxis: {
        type: 'value' as const,
        min: kickDomain.yMin,
        max: kickDomain.yMax,
        name: yLabel,
        nameLocation: 'middle' as const,
        nameGap: 40,
        axisLabel: { fontSize: 11, color: '#6B7280', formatter: formatAxisValue },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
      },
      visualMap: {
        min: 0,
        max: maxCount,
        calculable: false,
        show: false,
        // TrackMan Orange (light → full) for kick density.
        inRange: { color: ['#FCE8D9', '#EC691A'] },
        dimension: 0,
        seriesIndex: 0,
      },
      series: [{
        type: 'custom' as const,
        renderItem: (params: any, api: any) => {
          const topLeft = api.coord([api.value(1), api.value(4)]);
          const bottomRight = api.coord([api.value(2), api.value(3)]);
          const { x: cx, y: cy, width: cw, height: ch } = params.coordSys;
          const rx = Math.max(topLeft[0], cx);
          const ry = Math.max(topLeft[1], cy);
          const rRight = Math.min(bottomRight[0], cx + cw);
          const rBottom = Math.min(bottomRight[1], cy + ch);
          if (rx >= rRight || ry >= rBottom) return;
          return {
            type: 'rect',
            shape: { x: rx, y: ry, width: rRight - rx, height: rBottom - ry },
            style: api.style({ fill: api.visual('color'), stroke: 'rgba(255,255,255,0.2)' }),
          };
        },
        encode: { x: [1, 2], y: [3, 4], tooltip: 0 },
        data: heatmapData,
      }],
    };
  }, [heatmapData, kickDomain, xMetric, yMetric]);

  const activePreset = PRESETS.find((p) => p.x === xMetric && p.y === yMetric) ?? null;

  if (measurements.length === 0) return null;

  const metricOptions: MetricKey[] = ([
    'ballSpeedAvg',
    'distanceAvg',
    'hangTimeAvg',
    'gFactor',
    'spinRate',
    'spinAxis',
    'launchAngle',
    'launchDirection',
    'pullDirection',
    'maxHeight',
    'goalCrossingSpeed',
    'wallCrossingHeight',
  ] as MetricKey[]).filter((k) => !(hideDistance && k === 'distanceAvg') && !(hideSpin && k === 'spinRate'));

  const isHiddenMetric = (m: MetricKey) => (hideDistance && m === 'distanceAvg') || (hideSpin && m === 'spinRate');
  // A pairing no preset covers (picked via Custom) keeps the Custom chip lit while the popover is closed.
  const isCustomPairing = activePreset == null;
  const presets = PRESETS.filter((p) => !isHiddenMetric(p.x) && !isHiddenMetric(p.y));

  return (
    <div ref={containerRef} className='pp-compare-root'>
      <div className='pp-compare-controls-row'>
        <div className='pp-compare-presets'>
          {presets.map((p) => (
            <button
              key={p.labelKey}
              type='button'
              className={`pp-compare-preset-chip${!isCustomPairing && activePreset?.labelKey === p.labelKey ? ' pp-compare-preset-chip--active' : ''}`}
              onClick={() => { setXMetric(p.x); setYMetric(p.y); setCustomOpen(false); }}
            >
              {t(p.labelKey)}
            </button>
          ))}
          {/* Custom opens a small popover (like the date picker's) with the X / Y pickers. */}
          <span className='pp-popover-host' ref={customHostRef}>
            <button
              type='button'
              className={`pp-compare-preset-chip${isCustomPairing || customOpen ? ' pp-compare-preset-chip--active' : ''}`}
              onClick={() => setCustomOpen((open) => !open)}
              aria-expanded={customOpen}
              data-test='pp-compare-custom'
            >
              {t('playerPerformance.compareCustom')}
            </button>
            {customOpen && (
              <div className='pp-compare-popover' style={customPlacement} data-test='pp-compare-popover'>
                <div className='pp-compare-selector-group'>
                  <label className='pp-compare-label'>{t('playerPerformance.compareXAxis')}</label>
                  <select className='pp-compare-select' value={xMetric} onChange={(e) => setXMetric(e.target.value as MetricKey)}>
                    {metricOptions.map((k) => (
                      <option key={k} value={k}>{t(`playerPerformance.metric_${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div className='pp-compare-selector-group'>
                  <label className='pp-compare-label'>{t('playerPerformance.compareYAxis')}</label>
                  <select className='pp-compare-select' value={yMetric} onChange={(e) => setYMetric(e.target.value as MetricKey)}>
                    {metricOptions.map((k) => (
                      <option key={k} value={k}>{t(`playerPerformance.metric_${k}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </span>
        </div>
        <div className='pp-compare-mode-toggle'>
          <button
            type='button'
            className={`pp-compare-mode-btn${mode === 'scatter' ? ' pp-compare-mode-btn--active' : ''}`}
            onClick={() => setMode('scatter')}
          >
            {t('playerPerformance.modeScatter')}
          </button>
          <button
            type='button'
            className={`pp-compare-mode-btn${mode === 'heatmap' ? ' pp-compare-mode-btn--active' : ''}`}
            onClick={() => setMode('heatmap')}
          >
            {t('playerPerformance.modeHeatmap')}
          </button>
        </div>
      </div>
      <GrowChart option={mode === 'scatter' ? scatterOption : heatmapOption} notMerge />
      <div className='pp-scatter-legend'>
        {/* Only show a legend entry for statuses that actually occur in this data (hidden groups stay so they can be re-enabled). */}
        {RESULT_GROUPS.filter((g) => kickScatterByResult[g.key].length > 0).map((g) => {
          const isHidden = hiddenResults.has(g.key);
          const dotColor = isHidden ? '#D1D5DB' : (mode === 'scatter' ? resultColors[g.key] : '#EC691A');
          return (
            <button
              key={g.key}
              type='button'
              className={`pp-scatter-legend-item${isHidden ? ' pp-scatter-legend-item--hidden' : ''}`}
              onClick={() => toggleResult(g.key)}
            >
              <span className='pp-scatter-legend-dot' style={{ background: dotColor }} />
              {t(g.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MetricsCompareChart;
